/**
 * One phone per browser: the newest dashboard tab takes the phone over, unless
 * the tab holding it is on a call. That tab answers « refuse » and keeps the
 * phone; the newcomer stands down instead of cutting the call.
 *
 * Free of React and of the stores, so the protocol runs on plain
 * BroadcastChannels.
 */

type Utilisateur = number | string | undefined

export type MessageRelais =
  | { type: 'claim'; tabId: string; ts: number; userId: Utilisateur }
  | { type: 'refuse'; tabId: string; to: string; userId: Utilisateur }

export interface RelaisOnglet {
  tabId: string
  /** When this tab claimed the phone: the newest claim wins, unless the holder is on a call. */
  ts: number
  userId: Utilisateur
  /** This tab is on a call (ringing, connected, on hold, announcing). */
  enAppel: () => boolean
  /** A newer tab takes the phone: release it here. */
  ceder: () => void
  /** The tab on a call kept the phone: this one stands down. */
  refuse: () => void
}

/** What the protocol needs from a channel: a BroadcastChannel, or the server relay below. */
export interface CanalRelais {
  postMessage(message: MessageRelais): void
  addEventListener(type: 'message', ecouteur: (e: MessageEvent<MessageRelais>) => void): void
  removeEventListener(type: 'message', ecouteur: (e: MessageEvent<MessageRelais>) => void): void
}

/**
 * The same protocol through the server, for a phone the BroadcastChannel cannot
 * reach: the Callizy phone open inside HubSpot lives in a frame the browser keeps
 * apart. `envoyer` posts to /api/voice/relay/, which relays to the person's SSE
 * channel; `abonner` hears what comes back (`relais_telephone`).
 */
export function canalServeur(
  envoyer: (message: MessageRelais) => void,
  abonner: (rappel: (message: MessageRelais) => void) => () => void,
): CanalRelais {
  const desabonnements = new Map<(e: MessageEvent<MessageRelais>) => void, () => void>()
  return {
    postMessage: envoyer,
    addEventListener: (_type, ecouteur) => {
      desabonnements.set(
        ecouteur,
        abonner((message) => ecouteur({ data: message } as MessageEvent<MessageRelais>)),
      )
    },
    removeEventListener: (_type, ecouteur) => {
      desabonnements.get(ecouteur)?.()
      desabonnements.delete(ecouteur)
    },
  }
}

/** Claims the phone on `canal` and answers the other tabs. Returns the cleanup. */
export function ecouterRelais(canal: CanalRelais, relais: RelaisOnglet): () => void {
  const surMessage = (e: MessageEvent<MessageRelais>) => {
    const msg = e.data
    if (!msg || msg.userId !== relais.userId || msg.tabId === relais.tabId) return
    if (msg.type === 'claim') {
      if (msg.ts < relais.ts) return
      if (relais.enAppel()) {
        canal.postMessage({
          type: 'refuse',
          tabId: relais.tabId,
          to: msg.tabId,
          userId: relais.userId,
        } satisfies MessageRelais)
        return
      }
      relais.ceder()
    } else if (msg.type === 'refuse' && msg.to === relais.tabId) {
      relais.refuse()
    }
  }

  canal.addEventListener('message', surMessage)
  canal.postMessage({
    type: 'claim',
    tabId: relais.tabId,
    ts: relais.ts,
    userId: relais.userId,
  } satisfies MessageRelais)

  return () => canal.removeEventListener('message', surMessage)
}
